import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useAppStore } from '@core/store'
import {
  createWorkerClashEngine,
  runClash,
  type ClashEngine,
} from '@viewer/clash/lib'
import {
  deriveCategoryFilter,
  filterResults,
  resolveSide,
  sideCount,
  sidesOnlySameModel,
  unionIds,
  type ClashModelInventory,
  type SelectionLike,
  type SideMembers,
} from './clashSets'
import { evaluateClashRule, ruleKey } from './clashRules'
import { categoryLookup, clashModelSources, loadInventories } from './clashModels'
import { clashModelKey } from './store/clashSlice'
import { registarCorridaReconciliada } from './clashCorrida'
import { especialidadeDosModelos } from './clashExclusao'
import { filtrarAEntrada } from './clashFiltroEntrada'

// ---------------------------------------------------------------------------
// Correr a deteccao sem congelar a UI.
//
// O motor vai SEMPRE para o worker (`createWorkerClashEngine`): a
// narrow-phase de um modelo a serio bloqueava a thread principal e o 3D
// ficava preso, sem progresso e sem cancelar. O `dispose()` corre no
// `finally` e tambem na limpeza do efeito, para um painel fechado a meio
// nao deixar um worker vivo.
//
// Cancelar e pousar `signal.aborted = true`: o orquestrador para entre
// lotes, devolve os resultados parciais e marca `stats.cancelled`. Nao ha
// `terminate()` a meio, porque isso perdia os resultados ja apurados.
//
// A guarda de assinatura (`keyAtStart`) e a mesma do IDS: se os IFCs
// mudarem enquanto a analise corre, o resultado que vem a caminho e de
// modelos que ja nao estao carregados e nao pode aterrar no ecra.
// ---------------------------------------------------------------------------

export interface UseClashRun {
  inventories: ClashModelInventory[]
  inventoriesBusy: boolean
  membersA: SideMembers
  membersB: SideMembers
  countA: number
  countB: number
  /** Uma regra esta a ser avaliada neste momento (a contagem em vivo
   *  ainda nao e a final). */
  ruleBusy: boolean
  /** Quanto custou a ultima avaliacao de regra, em ms. 0 = veio da cache
   *  ou nunca correu. Mostrado na UI quando passa de meio segundo. */
  ruleMs: number
  /** Os dois lados podem cair no mesmo modelo e o self-clash esta
   *  desligado: a analise nao devolve nada e a UI tem de avisar. */
  sameModelWarning: boolean
  canRun: boolean
  run: () => Promise<void>
  cancel: () => void
}

/** Quanto tempo se espera depois da ultima tecla antes de correr uma
 *  regra. Meio segundo e o que separa "a contagem responde" de "cada
 *  tecla varre o modelo". */
const RULE_DEBOUNCE_MS = 500

const EMPTY_TARGETS: SelectionLike[] = []

export function useClashRun(opts: {
  active: boolean
  /** Quem corre: autor dos eventos da reconciliacao. */
  autor?: string | null
}): UseClashRun {
  const autorRef = useRef<string | null>(opts.autor ?? null)
  useEffect(() => {
    autorRef.current = opts.autor ?? null
  }, [opts.autor])
  const models = useAppStore((s) => s.models)
  const selection = useAppStore((s) => s.selectedTargets)
  const sideA = useAppStore((s) => s.clashSideA)
  const sideB = useAppStore((s) => s.clashSideB)
  // A tolerancia, a folga e o corte de contactos nao entram aqui: a
  // corrida le-os do store fresco no momento em que arranca, para nao
  // redeclarar o `run` a cada tecla escrita nos campos.
  const selfClash = useAppStore((s) => s.clashSelfClash)
  const running = useAppStore((s) => s.clashRunning)

  const [inventories, setInventories] = useState<ClashModelInventory[]>([])

  const engineRef = useRef<ClashEngine | null>(null)
  const signalRef = useRef<{ aborted: boolean } | null>(null)

  const modelKey = useMemo(() => clashModelKey(models), [models])

  // Inventario dos modelos (categorias com contagens reais). So se paga
  // quando o painel esta aberto: varrer as categorias de um modelo
  // grande custa uma ida ao worker do Fragments.
  // Nao ha bandeira `busy` em estado: pousar uma no corpo do efeito era
  // um setState sincrono (o que o react-hooks/set-state-in-effect proibe,
  // e com razao: dava um render a mais em cada abertura). O "a carregar"
  // deduz-se de o inventario ainda nao cobrir os modelos carregados.
  useEffect(() => {
    if (!opts.active) return
    let cancelled = false
    void loadInventories(models)
      .then((list) => {
        if (cancelled) return
        setInventories(list)
      })
      .catch((err) => {
        console.warn('[clash] inventario falhou', err)
      })
    return () => {
      cancelled = true
    }
  }, [opts.active, models])

  // Um worker pendurado depois de fechar o painel e um worker a segurar
  // memoria de geometria por nada.
  useEffect(
    () => () => {
      if (signalRef.current) signalRef.current.aborted = true
      engineRef.current?.dispose()
      engineRef.current = null
    },
    [],
  )

  // --- regras ---------------------------------------------------------
  // Correr uma regra e varrer os modelos (uma passagem por regra), por
  // isso NAO se corre a cada tecla: espera-se que a escrita assente. O
  // resultado fica em cache por (regra, modelos), portanto mudar a
  // tolerancia ou o agrupamento nao volta a varrer nada.
  const ruleKeyA = ruleKey(sideA.source === 'rule' ? sideA.rule : null)
  const ruleKeyB = ruleKey(sideB.source === 'rule' ? sideB.rule : null)
  const [ruleTargets, setRuleTargets] = useState<
    Record<string, SelectionLike[]>
  >({})
  const [ruleBusy, setRuleBusy] = useState(false)
  const [ruleMs, setRuleMs] = useState(0)

  useEffect(() => {
    const wanted = [ruleKeyA, ruleKeyB].filter((k) => k !== '')
    if (!opts.active || wanted.length === 0) return
    let cancelled = false
    const timer = setTimeout(() => {
      setRuleBusy(true)
      void (async () => {
        const out: Record<string, SelectionLike[]> = {}
        let worst = 0
        for (const key of new Set(wanted)) {
          const query = ruleKeyA === key ? sideA.rule : sideB.rule
          const res = await evaluateClashRule(query, modelKey)
          out[key] = res.targets
          worst = Math.max(worst, res.ms)
        }
        if (cancelled) return
        setRuleTargets((prev) => ({ ...prev, ...out }))
        setRuleMs(worst)
        setRuleBusy(false)
      })()
    }, RULE_DEBOUNCE_MS)
    return () => {
      cancelled = true
      clearTimeout(timer)
    }
    // `sideA.rule` / `sideB.rule` entram pela chave: duas regras com a
    // mesma chave dao o mesmo resultado e nao precisam de reavaliacao.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [opts.active, ruleKeyA, ruleKeyB, modelKey])

  const targetsA = ruleTargets[ruleKeyA] ?? EMPTY_TARGETS
  const targetsB = ruleTargets[ruleKeyB] ?? EMPTY_TARGETS

  const membersA = useMemo(
    () => resolveSide(sideA, inventories, selection, targetsA),
    [sideA, inventories, selection, targetsA],
  )
  const membersB = useMemo(
    () => resolveSide(sideB, inventories, selection, targetsB),
    [sideB, inventories, selection, targetsB],
  )
  const countA = sideCount(membersA)
  const countB = sideCount(membersB)
  const sameModelWarning =
    !selfClash && countA > 0 && countB > 0 && sidesOnlySameModel(membersA, membersB)
  const canRun = countA > 0 && countB > 0 && !running

  const cancel = useCallback(() => {
    if (signalRef.current) signalRef.current.aborted = true
  }, [])

  const run = useCallback(async () => {
    const store = useAppStore.getState()
    if (store.clashRunning) return
    // As regras ja foram avaliadas para a contagem em vivo e o resultado
    // esta em cache: pedi-las outra vez aqui nao custa varrimento nenhum
    // e garante que se corre exactamente o que o painel mostrou. O
    // `await` so tem peso na primeira vez (regra escrita e corrida de
    // imediato, sem esperar pela contagem).
    const [ra, rb] = await Promise.all([
      evaluateClashRule(
        store.clashSideA.source === 'rule' ? store.clashSideA.rule : null,
        modelKey,
      ),
      evaluateClashRule(
        store.clashSideB.source === 'rule' ? store.clashSideB.rule : null,
        modelKey,
      ),
    ])
    const a = resolveSide(
      store.clashSideA,
      inventories,
      store.selectedTargets,
      ra.targets,
    )
    const b = resolveSide(
      store.clashSideB,
      inventories,
      store.selectedTargets,
      rb.targets,
    )
    if (sideCount(a) === 0 || sideCount(b) === 0) {
      store.failClashRun('Define os dois conjuntos antes de correr.')
      return
    }
    const keyAtStart = modelKey
    store.startClashRun()

    const ids = unionIds(a, b)
    const sources = clashModelSources(Object.keys(ids))
    const signal = { aborted: false }
    signalRef.current = signal
    const engine = createWorkerClashEngine()
    engineRef.current = engine

    try {
      const outcome = await runClash(sources, {
        engine,
        signal,
        ids,
        categoryOf: categoryLookup(inventories),
        config: {
          tolerance: store.clashTolerance,
          clearance: store.clashClearance,
          selfClash: store.clashSelfClash,
          categoryFilter: deriveCategoryFilter(
            store.clashSideA,
            store.clashSideB,
            inventories,
          ),
        },
        onProgress: (p) => {
          useAppStore.getState().setClashProgress(p)
        },
      })
      const latest = useAppStore.getState()
      if (clashModelKey(latest.models) !== keyAtStart) return
      // A matriz A x B, e depois o filtro a entrada (o primeiro passo do
      // funil): folga so nos pares escolhidos, penetracao minima e
      // contactos. Cada corte conta, para o painel dizer porque.
      const naMatriz = filterResults(outcome.results, { a, b })
      const entrada = filtrarAEntrada(naMatriz, {
        especialidadeDe: especialidadeDosModelos(latest.models, latest.clashClassificacao.especialidades),
        folgaPares: store.clashFolgaPares,
        penetracaoMinima: store.clashPenetracaoMinima,
        ignoreContacts: store.clashIgnoreContacts,
      })
      const filtered = entrada.mantidos
      latest.finishClashRun(filtered, outcome.stats, keyAtStart)
      latest.setClashExcluidasEntrada(entrada.excluidas)
      // Reconciliar a triagem com esta corrida e registar a corrida no
      // historico (a sessao leva-o). Falhar aqui nao estraga a corrida.
      try {
        await registarCorridaReconciliada({
          resultados: filtered,
          config: {
            tolerance: store.clashTolerance,
            clearance: store.clashClearance,
            selfClash: store.clashSelfClash,
            sideA: store.clashSideA,
            sideB: store.clashSideB,
            penetracaoMinima: store.clashPenetracaoMinima,
            folgaPares: [...store.clashFolgaPares],
            ignoreContacts: store.clashIgnoreContacts,
          },
          cancelada: outcome.stats.cancelled,
          autor: autorRef.current,
        })
      } catch (err) {
        console.warn('[clash] reconciliação falhou', err)
      }
    } catch (err) {
      const latest = useAppStore.getState()
      if (clashModelKey(latest.models) !== keyAtStart) return
      latest.failClashRun(
        err instanceof Error ? err.message : 'Falha a detectar conflitos.',
      )
    } finally {
      engine.dispose()
      if (engineRef.current === engine) engineRef.current = null
      if (signalRef.current === signal) signalRef.current = null
    }
  }, [inventories, modelKey])

  return {
    inventories,
    inventoriesBusy: models.length > 0 && inventories.length !== models.length,
    membersA,
    membersB,
    countA,
    countB,
    ruleBusy,
    ruleMs,
    sameModelWarning,
    canRun,
    run,
    cancel,
  }
}
